import React, { FC, useEffect, useState } from "react";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "redux/store";
import {
  getallupdateProperties,
  updateProperties,
} from "../../redux/reducers/Properties/PropertiesSlice";

import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import FormItem from "./FormItem";
import Input from "shared/Input/Input";
import { ListingDetailsvalidationSchema } from "utils/formSchema";
// NOTE: Make sure ListingDetailsvalidationSchema does NOT include total_rooms validation
import CommonLayoutV2 from "./CommonLayoutV2";
import __countryListing from "../../data/jsons/__countryListing.json";
import { calculateStepCompletion, checkExistingCompletion } from "utils/helpers/listingCompletionHelper";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import { captureError } from "utils/sentry";

export interface ListingDetailsProps {}

interface InitialValues {
  property_size: string;
  total_bedrooms: string;
  total_bathrooms: string;
  additionalRules: AdditionalRules[];
  roomies_attributes: any;
}

interface AdditionalRules {
  id: number | null;
  name: string;
}

const ListingDetails: FC<ListingDetailsProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { PropertyToEdit } = useSelector((state: RootState) => state.property);
  const { slug } = useParams();
  const id = slug;
  const RouteID = id;
  
  const [isEdit, setIsEdit] = useState<{
    state: boolean;
    itemId: number | null;
  }>({ state: false, itemId: null });
  
  const [initialValues, setInitialValues] = useState<InitialValues>({
    property_size: "",
    total_bedrooms: "",
    total_bathrooms: "",
    additionalRules: [],
    roomies_attributes: [],
  });
  
  const [newRuleInput, setNewRuleInput] = useState<string | never>("");
  const [loading, setLoading] = React.useState(true);

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false },
    2: { completed: false },
    3: { completed: false },
    4: { completed: false, fieldsCompleted: 0, totalFields: 3 },
    5: { completed: false },
    6: { completed: false },
    7: { completed: false },
    8: { completed: false },
    9: { completed: false },
  });

  useEffect(() => {
    const propertyId = id;
    if (propertyId) {
      dispatch(getallupdateProperties({ propertyId }))
        .unwrap()
        .then((response) => {
          if (response?.success && response.property) {
            setInitialValues({
              property_size: response?.property?.property_size || "",
              total_bedrooms: response?.property?.total_bedrooms || "",
              total_bathrooms: response?.property?.total_bathrooms || "",
              additionalRules: response?.property?.additional_rules,
              roomies_attributes: response?.property?.roomies,
            });
          }
        })
       .catch((error) => {
  toast.error("Failed to load property data. Please refresh.");
  captureError(error, { stage: "listing_details_fetch", propertyId: id });
});
    }
  }, [dispatch, id]);

  const handleAddRule = (setFieldValue: any, values: any) => {
    if (newRuleInput?.length === 0) return null;

    let newRule = {
      id: Date.now(),
      name: newRuleInput,
      isNew: true,
    };

    if (isEdit.state) {
      const modifiedRule = values.additionalRules?.map((item: any) =>
        item.id === isEdit.itemId ? { ...item, name: newRuleInput } : item
      );
      setFieldValue("additionalRules", modifiedRule);
    } else {
      setFieldValue("additionalRules", [...values.additionalRules, newRule]);
    }
    setIsEdit({ state: false, itemId: null });
    setNewRuleInput("");
  };

  const handleRemoveRule = (id: number, setFieldValue: any, values: any) => {
    const remainingAdditionRule = values.additionalRules?.filter(
      (item: AdditionalRules) => item.id !== id
    );
    if (isEdit.state) {
      setNewRuleInput("");
    }
    setFieldValue("additionalRules", remainingAdditionRule);
    setIsEdit({ state: false, itemId: null });
  };

  const handleEditRule = (rule: any) => {
    if (rule?.name) {
      setIsEdit({ state: true, itemId: rule.id });
      setNewRuleInput(rule.name);
    }
  };

  const renderNoInclude = (
    rule: any,
    index: number,
    setFieldValue: any,
    values: any
  ) => (
    <div
      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
      key={`${index} ${rule.id}`}
    >
      <span className="text-gray-900 dark:text-white font-medium capitalize">
        {rule.name}
      </span>
      <div className="flex gap-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={() => handleEditRule(rule)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
          title="Edit Rule"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => handleRemoveRule(rule.id, setFieldValue, values)}
          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Delete Rule"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );

  const handleAddRoomie = (values: any, setFieldValue: any) => {
    setFieldValue("roomies_attributes", [
      ...values.roomies_attributes,
      { id: Date.now(), name: "", gender: "", country: "", isAlready: false },
    ]);
  };

  const handleInputChange = (
    id: number,
    field: any,
    value: any,
    values: any,
    setFieldValue: any
  ) => {
    const updatedRoomies = values.roomies_attributes.map((roomie: any) =>
      roomie.id === id ? { ...roomie, [field]: value } : roomie
    );
    setFieldValue("roomies_attributes", updatedRoomies);
  };

  const handleRemoveRoomie = (id: number, values: any, setFieldValue: any) => {
    setFieldValue(
      "roomies_attributes",
      values.roomies_attributes.filter((roomie: any) => roomie.id !== id)
    );
  };

  const isDirty = (values: InitialValues, initialValues: InitialValues) =>
    (Object.keys(values) as (keyof InitialValues)[]).some(
      (key) => values[key] !== initialValues[key]
    );

  const submitHandler = (values: typeof initialValues) => {
    const dirty = isDirty(values, initialValues);
    const modifiedRoomies = values.roomies_attributes?.map((item: any) => ({
      name: item.name,
      gender: item.gender,
      country: item.country,
    }));

    const modified = values.additionalRules?.map((item: any) => ({
      ...item,
      id: null,
    }));

    const payload = {
      id: id,
      property_size: values.property_size,
      total_bathrooms: values.total_bathrooms,
      total_bedrooms: values.total_bedrooms,
      additional_rules_attributes: modified,
      roomies_attributes: modifiedRoomies,
    };

    if (dirty) {
      dispatch(updateProperties({ property: payload }))
        .unwrap()
        .then((response) => {
  if (response?.success) {
    toast.success(response?.message || "Updated successfully!");
    navigate(`/add-listing-5/${slug}`);
  } else {
    toast.error("Failed to save. Please try again.");
  }
})
.catch((error) => {
  toast.error("Something went wrong. Please try again.");
  captureError(error, { stage: "listing_details_update", propertyId: id });
});
    } else {
      navigate(`/add-listing-5/${slug}`);
    }
  };

  useEffect(() => {
    setTimeout(() => setLoading(false), 1000);
  }, []);

  // Load completion status from PropertyToEdit using helper
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);
      
      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

  // Track form values for completion calculation
  const [formValues, setFormValues] = useState<any>(null);
  const [formErrors, setFormErrors] = useState<any>({});

  // Calculate completion for Step 4 whenever form values change
  useEffect(() => {
    if (formValues) {
      const requiredFields = ["property_size", "total_bedrooms", "total_bathrooms"];
      const hasErrors = Object.keys(formErrors).length > 0;
      const completion = calculateStepCompletion(formValues, requiredFields, hasErrors);

      setStepCompletionStatus((prev) => ({
        ...prev,
        4: completion,
      }));
    }
  }, [formValues, formErrors]);

  return (
    <CommonLayoutV2 
      currentHref="/add-listing-4" 
      PropertyID={RouteID}
      propertyType={PropertyToEdit?.property_type?.name}
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Header Section */}
        <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Property Details
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Provide specific details about your property's size, rooms, and house rules.
          </p>
        </div>

        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={ListingDetailsvalidationSchema}
          onSubmit={submitHandler}
        >
          {({
            errors,
            values,
            touched,
            isSubmitting,
            getFieldProps,
            setFieldValue,
            handleSubmit,
          }) => {
            // Update form values and errors for completion tracking (outside of hooks)
            if (JSON.stringify(values) !== JSON.stringify(formValues)) {
              setFormValues(values);
            }
            if (JSON.stringify(errors) !== JSON.stringify(formErrors)) {
              setFormErrors(errors);
            }

            return (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Property Specifications Card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                      1
                    </span>
                    Property Specifications *
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormItem label="Property Size (m²)" desc="Total square meters of your property">
                      <Input
                        type="text"
                        placeholder="e.g., 85"
                        {...getFieldProps("property_size")}
                        className="text-base"
                      />
                      {errors.property_size && touched.property_size && (
                        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.property_size}
                        </div>
                      )}
                    </FormItem>

                    <FormItem label="Bedrooms" desc="Number of bedrooms available">
                      <Input
                        type="text"
                        placeholder="e.g., 2"
                        {...getFieldProps("total_bedrooms")}
                        className="text-base"
                      />
                      {errors.total_bedrooms && touched.total_bedrooms && (
                        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.total_bedrooms}
                        </div>
                      )}
                    </FormItem>

                    <FormItem label="Bathrooms" desc="Number of bathrooms in the property">
                      <Input
                        type="text"
                        placeholder="e.g., 2"
                        {...getFieldProps("total_bathrooms")}
                        className="text-base"
                      />
                      {errors.total_bathrooms && touched.total_bathrooms && (
                        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.total_bathrooms}
                        </div>
                      )}
                    </FormItem>
                  </div>
                </div>

                {/* Property Rules Card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 flex items-center justify-center text-sm font-bold">
                      2
                    </span>
                    Property Rules
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 ml-10">
                    Set house rules to ensure guests understand expectations. Common rules include no smoking, no pets, or quiet hours.
                  </p>

                  <div className="space-y-4">
                    {loading ? (
                      <div className="space-y-3">
                        {Array.from({
                          length: values.additionalRules.length || 3,
                        }).map((_, index) => (
                          <div
                            key={index}
                            className="h-14 bg-neutral-200 dark:bg-neutral-700 rounded-lg animate-pulse"
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {values.additionalRules.length > 0 ? (
                          values.additionalRules.map((rule, index) =>
                            renderNoInclude(rule, index, setFieldValue, values)
                          )
                        ) : (
                          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p>No rules added yet. Add your first house rule below.</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                      <Input
                        type="text"
                        className="flex-1 text-base capitalize"
                        placeholder="e.g., No smoking, No parties, Quiet hours after 10 PM..."
                        value={newRuleInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          return setNewRuleInput(e.target.value);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddRule(setFieldValue, values);
                          }
                        }}
                      />
                      <ButtonPrimary
                        type="button"
                        className="flex-shrink-0 w-full sm:w-auto px-6"
                        onClick={() => handleAddRule(setFieldValue, values)}
                        disabled={!newRuleInput.trim()}
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {isEdit.state ? "Update Rule" : "Add Rule"}
                      </ButtonPrimary>
                    </div>
                  </div>
                </div>

                {/* Roommates Card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 flex items-center justify-center text-sm font-bold">
                      3
                    </span>
                    Current Roommates
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 ml-10">
                    Let potential guests know who they'll be living with. This helps them make informed decisions.
                  </p>

                  <div className="space-y-4">
                    {loading ? (
                      <div className="space-y-4">
                        {Array.from({
                          length: values?.roomies_attributes.length || 2,
                        }).map((_, index) => (
                          <div key={index} className="h-32 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse" />
                        ))}
                      </div>
                    ) : values?.roomies_attributes?.length > 0 ? (
                      <div className="space-y-4">
                        {values.roomies_attributes.map((roomie: any, index: number) => (
                          <div
                            key={roomie.id}
                            className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-5"
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#F56948] text-white flex items-center justify-center font-bold">
                                  {index + 1}
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                  Roommate {index + 1}
                                </h4>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveRoomie(roomie.id, values, setFieldValue)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Name *
                                </label>
                                <Input
                                  type="text"
                                  value={roomie.name}
                                  onChange={(e) =>
                                    handleInputChange(
                                      roomie.id,
                                      "name",
                                      e.target.value,
                                      values,
                                      setFieldValue
                                    )
                                  }
                                  placeholder="Enter name"
                                  className={`capitalize ${
                                    !roomie.name ? "border-red-500" : ""
                                  }`}
                                />
                                {!roomie.name && (
                                  <div className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    Required
                                  </div>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Gender *
                                </label>
                                <select
                                  value={roomie.gender}
                                  onChange={(e) =>
                                    handleInputChange(
                                      roomie.id,
                                      "gender",
                                      e.target.value,
                                      values,
                                      setFieldValue
                                    )
                                  }
                                  className={`block w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                                    !roomie.gender ? "border-red-500" : "border-gray-300 dark:border-gray-600"
                                  }`}
                                >
                                  <option value="">Select gender</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                  <option value="Other">Other</option>
                                </select>
                                {!roomie.gender && (
                                  <div className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    Required
                                  </div>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Country *
                                </label>
                                <select
                                  value={roomie.country}
                                  onChange={(e) =>
                                    handleInputChange(
                                      roomie.id,
                                      "country",
                                      e.target.value,
                                      values,
                                      setFieldValue
                                    )
                                  }
                                  className={`block w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                                    !roomie.country ? "border-red-500" : "border-gray-300 dark:border-gray-600"
                                  }`}
                                >
                                  <option value="">Select country</option>
                                  {__countryListing.map((country) => (
                                    <option key={country.name} value={country.name}>
                                      {country.name}
                                    </option>
                                  ))}
                                </select>
                                {!roomie.country && (
                                  <div className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    Required
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="text-lg mb-2">No roommates added yet</p>
                        <p className="text-sm">Add roommates to let guests know who they'll be living with</p>
                      </div>
                    )}

                    <ButtonPrimary
                      type="button"
                      className="w-full sm:w-auto mt-4"
                      onClick={() => handleAddRoomie(values, setFieldValue)}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Roommate
                    </ButtonPrimary>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Step 4 of 9 • Property details and specifications
                  </div>
                  <div className="flex gap-3">
                    <ButtonSecondary
                      href={`/add-listing-3/${slug}`}
                      disabled={isSubmitting}
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Go Back
                      </span>
                    </ButtonSecondary>
                    <ButtonPrimary
                      type="submit"
                      disabled={isSubmitting}
                      className="min-w-[140px]"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Continue
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </CommonLayoutV2>
  );
};

export default ListingDetails;